import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { ApiRequestError } from "@src/packages/api/api-error";
import { toErrorMessage } from "@src/packages/api/toErrorMessage";
import {
  useActiveCycle,
  useCreateCycle,
  type Cycle,
  type CycleType as ApiCycleType,
} from "@src/packages/cycles/hooks/useCycles";
import {
  Dialog,
  DialogActions,
  DialogHeader,
  PrimaryButton,
  SecondaryButton,
} from "@louise/moon-ui";
import { frozenSpecimenTreatmentMatches } from "@src/packages/care/cryovault/frozenSpecimenTreatmentTarget";
import { useTranslations } from "@src/packages/i18n";
import { toLocalDateString } from "@src/packages/utils/date";
import { buildCycleContextSnapshot } from "@src/packages/experience/buildCycleContextSnapshot";
import { experienceContextQueryKey } from "@src/packages/experience/hooks/useExperienceContext";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";
import {
  MoonStartTreatmentModal,
  type CycleType as StartTreatmentCycleType,
  type FrozenPrefill,
  type StartTreatmentFormData,
  type StartTreatmentSubmitError,
} from "./screens/care/modals/MoonStartTreatmentModal";
import {
  createClientRequestId,
  useAcceptEmbryoFreezingPlan,
  useCreateEmbryoFreezingPlan,
} from "@src/packages/embryo-freezing-plans/hooks/useEmbryoFreezingPlans";
import { createMinimalEmbryoFreezingPlanInput } from "@src/packages/embryo-freezing-plans/minimalStartPlan";

type CreatedCycle = Partial<Cycle> & { id?: string };

/**
 * Why the last start didn't go through. Shown in place in the start-treatment
 * dialog: Alert.alert is a no-op on the web build, so it was never seen there.
 */
type StartFailure =
  "choose_treatment" | "frozen_selection_mismatch" | "unable_to_start";

type StartTreatmentRequest = {
  goal?: "preserve" | "pregnancy";
  gender?: "male" | "female" | null;
  presetCycleType?: StartTreatmentCycleType | null;
  frozenPrefill?: FrozenPrefill | null;
  source?: string;
  onCreated?: (cycle: CreatedCycle | undefined) => void;
  /** Called when the user has an open cycle and chooses to open it instead of starting a new one. */
  onOpenActiveCycle?: (cycle: Cycle) => void;
};

type StartTreatmentControllerValue = {
  openStartTreatment: (request?: StartTreatmentRequest) => void;
  closeStartTreatment: () => void;
  isStartingTreatment: boolean;
};

const StartTreatmentControllerContext =
  createContext<StartTreatmentControllerValue | null>(null);

function unwrapCreatedCycle(result: unknown): CreatedCycle | undefined {
  const raw = (result as { data?: unknown } | null | undefined)?.data;
  const nested =
    raw && typeof raw === "object" && "data" in raw
      ? (raw as { data?: unknown }).data
      : raw;

  if (!nested || typeof nested !== "object") return undefined;
  return nested as CreatedCycle;
}

const API_CYCLE_TYPES = new Set<ApiCycleType>([
  "ivf",
  "iui",
  "fet",
  "icsi",
  "oi",
  "egg_freezing",
  "embryo_freezing",
  "sperm_freezing",
]);

function isApiCycleType(value: StartTreatmentCycleType): value is ApiCycleType {
  return API_CYCLE_TYPES.has(value);
}

export function StartTreatmentControllerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const t = useTranslations();
  const queryClient = useQueryClient();
  const { tenantId, patientId } = useApiClient();
  const authorizedExperience = useAuthorizedExperienceProjection();
  const createCycle = useCreateCycle();
  const createEmbryoFreezingPlan = useCreateEmbryoFreezingPlan();
  const acceptEmbryoFreezingPlan = useAcceptEmbryoFreezingPlan();
  const [request, setRequest] = useState<StartTreatmentRequest | null>(null);
  const [blockedByActiveCycle, setBlockedByActiveCycle] =
    useState<StartTreatmentRequest | null>(null);
  const [startFailure, setStartFailure] = useState<StartFailure | null>(null);
  const activeCycleQuery = useActiveCycle();

  /**
   * Set when the patient closed the modal while a write was still in flight, so
   * the failure that write reports later is not announced to someone who has
   * already walked away from it.
   */
  const dismissedWhileWritingRef = useRef(false);

  /**
   * Closes even while a write is in flight.
   *
   * Refusing to close was meant to protect a cycle mid-creation, but it made an
   * unanswered request inescapable: the spinner ran on, the close button and the
   * backdrop were both refused, and only a page reload got the patient out. A
   * request that cannot be waited out has to be leavable. The write is not
   * cancelled — it continues, and the cycle it creates still lands.
   */
  const closeStartTreatment = useCallback(() => {
    if (
      createCycle.isPending ||
      createEmbryoFreezingPlan.isPending ||
      acceptEmbryoFreezingPlan.isPending
    ) {
      dismissedWhileWritingRef.current = true;
    }
    setStartFailure(null);
    setRequest(null);
  }, [
    acceptEmbryoFreezingPlan.isPending,
    createCycle.isPending,
    createEmbryoFreezingPlan.isPending,
  ]);

  const openStartTreatment = useCallback(
    (nextRequest: StartTreatmentRequest = {}) => {
      dismissedWhileWritingRef.current = false;
      setStartFailure(null);
      const normalized: StartTreatmentRequest = {
        goal: nextRequest.goal ?? "pregnancy",
        gender: nextRequest.gender ?? null,
        presetCycleType: nextRequest.presetCycleType ?? null,
        frozenPrefill: nextRequest.frozenPrefill ?? null,
        source: nextRequest.source,
        onCreated: nextRequest.onCreated,
        onOpenActiveCycle: nextRequest.onOpenActiveCycle,
      };
      // Existing open cycle blocks a new one: guide the user to finish it first
      // instead of letting the API reject the create silently.
      const openCycle = activeCycleQuery.data;
      if (openCycle && openCycle.status !== "closed") {
        setBlockedByActiveCycle(normalized);
        return;
      }
      setRequest(normalized);
    },
    [activeCycleQuery.data],
  );

  const dismissBlockedByActiveCycle = useCallback(() => {
    setBlockedByActiveCycle(null);
  }, []);

  const handleOpenActiveCycle = useCallback(() => {
    const cycle = activeCycleQuery.data;
    const callback = blockedByActiveCycle?.onOpenActiveCycle;
    setBlockedByActiveCycle(null);
    if (cycle && callback) callback(cycle);
  }, [activeCycleQuery.data, blockedByActiveCycle]);

  const handleStartTreatment = useCallback(
    async (formData: StartTreatmentFormData) => {
      setStartFailure(null);
      if (!isApiCycleType(formData.treatmentType)) {
        setStartFailure("choose_treatment");
        return;
      }

      const selection = formData.frozenSelection ?? request?.frozenPrefill;
      const selectionMatchesTreatment =
        !selection ||
        frozenSpecimenTreatmentMatches(selection.type, formData.treatmentType);

      if (!selectionMatchesTreatment) {
        setStartFailure("frozen_selection_mismatch");
        return;
      }

      const startDateStr = formData.startDate
        ? toLocalDateString(formData.startDate)
        : null;

      try {
        let cyclePlan: Awaited<
          ReturnType<typeof createEmbryoFreezingPlan.mutateAsync>
        > | null = null;
        if (formData.treatmentType === "embryo_freezing") {
          const planInput = createMinimalEmbryoFreezingPlanInput(
            createClientRequestId(),
          );
          const draftPlan =
            await createEmbryoFreezingPlan.mutateAsync(planInput);
          cyclePlan = await acceptEmbryoFreezingPlan.mutateAsync({
            planId: draftPlan.id,
            input: {
              clientRequestId: createClientRequestId(),
              expectedVersion: draftPlan.currentVersion,
            },
          });
        }

        const acceptedPlanVersion = cyclePlan?.versions.find(
          (candidate) => candidate.version === cyclePlan.currentVersion,
        );
        const planMaterialIds =
          acceptedPlanVersion?.materials
            .filter((material) => material.allocationStatus === "selected")
            .map((material) => material.materialEntityId) ?? [];
        // Also carry the specimens she picked from the CryoVault. These may
        // not appear on a formal treatment plan (the plan flow only runs
        // for embryo_freezing here), but they are the "selected frozen
        // material" the treatment-hub phase graph reads from to decide
        // whether the cycle opens on thaw or on stimulation + retrieval.
        const materialIds = Array.from(
          new Set([...planMaterialIds, ...(selection?.specimenIds ?? [])]),
        );

        const acceptedTreatmentPlan =
          authorizedExperience.canonical?.acceptedTreatmentPlan;
        const cyclePlanSnapshot =
          acceptedTreatmentPlan?.status === "accepted" &&
          (formData.treatmentType === "iui" ||
            formData.treatmentType === "oi") &&
          acceptedTreatmentPlan.treatmentFamily === formData.treatmentType
            ? {
                acceptedPlanId: acceptedTreatmentPlan.id,
                acceptedPlanVersion: acceptedTreatmentPlan.version,
                clientRequestId: createClientRequestId(),
              }
            : undefined;

        // Build the context snapshot: from the canonical experience when it
        // exists, otherwise minimally from the frozen material she picked.
        // The frozen selection must reach the cycle record either way — it
        // is what the treatment hub uses to render the thaw flow instead of
        // stimulation + retrieval, and a CryoVault-initiated cycle often
        // has no canonical experience to hang the snapshot on.
        const contextSnapshotForCreate = authorizedExperience.canonical
          ? buildCycleContextSnapshot(
              authorizedExperience.canonical,
              materialIds,
            )
          : materialIds.length > 0
            ? {
                acceptedTreatmentPlanId: null,
                acceptedTreatmentPlanVersion: null,
                clinicalSubjectPatientId: null,
                participantIds: [],
                materialIds,
                status: "missing" as const,
                snapshotSource: "start-treatment-from-vault",
              }
            : undefined;
        const createResult = await createCycle.mutateAsync({
          cycleType: formData.treatmentType,
          provider: formData.clinicName || null,
          startDate: startDateStr,
          status: "open",
          ...(contextSnapshotForCreate
            ? { contextSnapshot: contextSnapshotForCreate }
            : {}),
          ...(cyclePlan
            ? {
                embryoFreezingPlanSnapshot: {
                  planId: cyclePlan.id,
                  expectedVersion: cyclePlan.currentVersion,
                  clientRequestId: createClientRequestId(),
                },
              }
            : {}),
          ...(cyclePlanSnapshot ? { cyclePlanSnapshot } : {}),
        });
        const createdCycle = unwrapCreatedCycle(createResult);

        if (!createdCycle?.id) {
          throw new Error("Cycle creation returned no cycle id");
        }

        queryClient.setQueryData(
          ["active-cycle", tenantId, patientId],
          createdCycle,
        );
        // Also seed the cycles list. CycleDetail resolves a cycle from the
        // list by id (via useActiveCycleWithPhases); without this the new
        // cycle is missing on arrival and CycleDetail briefly renders the
        // "post-cycle" fallback based on the previous cycle's outcome
        // until the background refetch lands (~500ms flash).
        const seededCycle = createdCycle;
        queryClient.setQueryData<Cycle[]>(
          ["cycles", tenantId, patientId],
          (previous) => {
            const next = previous ? [...previous] : [];
            if (!next.some((entry) => entry.id === seededCycle.id)) {
              next.unshift(seededCycle as Cycle);
            }
            return next;
          },
        );

        // Mark stale without awaiting refetches: on a slow dev API the six
        // sequential refetches keep the button in a loading state for tens
        // of seconds. The active-cycle cache is already populated
        // optimistically (`setQueryData` above), so downstream screens
        // render immediately and refetch in the background.
        void queryClient.invalidateQueries({ queryKey: ["active-cycle"] });
        void queryClient.invalidateQueries({ queryKey: ["cycles"] });
        void queryClient.invalidateQueries({ queryKey: ["cycle-phases"] });
        void queryClient.invalidateQueries({ queryKey: ["medications"] });
        void queryClient.invalidateQueries({ queryKey: ["specimens"] });
        void queryClient.invalidateQueries({
          queryKey: experienceContextQueryKey(patientId),
        });

        const onCreated = request?.onCreated;
        setRequest(null);
        // Defer navigation so the modal has committed unmount before the
        // route changes. Without this, on web the Modal overlay can linger
        // over the new screen and the user stays visually on CryoVault.
        if (onCreated) {
          setTimeout(() => onCreated(createdCycle), 0);
        }
      } catch (error) {
        // Log the normalized status and message, not just the object. The
        // patient-facing alert stays deliberately generic, so this line is the
        // only place the actual reason is observable.
        const status =
          error instanceof ApiRequestError && error.status !== undefined
            ? ` (HTTP ${error.status})`
            : "";
        console.error(
          `[StartTreatmentController] Failed to start cycle${
            request?.source ? ` from ${request.source}` : ""
          }${status}: ${toErrorMessage(error)}`,
          error,
        );
        // Still logged above, but not shown: the patient closed this while it
        // was in flight, so there is no dialog left to show it in.
        if (!dismissedWhileWritingRef.current) {
          setStartFailure("unable_to_start");
        }
      } finally {
        dismissedWhileWritingRef.current = false;
      }
    },
    [
      createCycle,
      createEmbryoFreezingPlan,
      acceptEmbryoFreezingPlan,
      patientId,
      queryClient,
      request,
      tenantId,
      authorizedExperience,
    ],
  );

  const clearStartFailure = useCallback(() => setStartFailure(null), []);
  const submitError = useMemo((): StartTreatmentSubmitError | null => {
    switch (startFailure) {
      case "choose_treatment":
        return {
          title: t("care.overview.alerts.choose_treatment_title"),
          message: t("care.overview.alerts.choose_treatment_message"),
          canRetry: false,
        };
      case "frozen_selection_mismatch":
        return {
          title: t("care.overview.alerts.frozen_selection_mismatch_title"),
          message: t("care.overview.alerts.frozen_selection_mismatch_message"),
          canRetry: false,
        };
      case "unable_to_start":
        return {
          title: t("care.overview.alerts.unable_to_start_title"),
          message: t("care.overview.alerts.unable_to_start_message"),
          canRetry: true,
        };
      default:
        return null;
    }
  }, [startFailure, t]);

  const value = useMemo(
    () => ({
      openStartTreatment,
      closeStartTreatment,
      isStartingTreatment:
        createCycle.isPending ||
        createEmbryoFreezingPlan.isPending ||
        acceptEmbryoFreezingPlan.isPending,
    }),
    [
      closeStartTreatment,
      acceptEmbryoFreezingPlan.isPending,
      createCycle.isPending,
      createEmbryoFreezingPlan.isPending,
      openStartTreatment,
    ],
  );

  return (
    <StartTreatmentControllerContext.Provider value={value}>
      {children}
      <MoonStartTreatmentModal
        visible={request !== null}
        onClose={closeStartTreatment}
        onStartTreatment={handleStartTreatment}
        isLoading={
          createCycle.isPending ||
          createEmbryoFreezingPlan.isPending ||
          acceptEmbryoFreezingPlan.isPending
        }
        goal={request?.goal ?? "pregnancy"}
        gender={request?.gender ?? null}
        presetCycleType={request?.presetCycleType ?? null}
        frozenPrefill={request?.frozenPrefill ?? null}
        submitError={submitError}
        onFormChange={clearStartFailure}
      />
      <Dialog
        visible={blockedByActiveCycle !== null}
        onClose={dismissBlockedByActiveCycle}
        layout="centered"
        closeButton="minimal"
        testID="cycle-already-open-dialog"
      >
        <DialogHeader
          title={t(
            "care.start_treatment.cycle_already_open.you_have_open_cycle",
          )}
          subtitle={t(
            "care.start_treatment.cycle_already_open.finish_before_starting_new",
          )}
          style={{ marginBottom: 18 }}
        />
        <DialogActions style={{ marginTop: 0 }}>
          <SecondaryButton
            label={t("care.start_treatment.cycle_already_open.not_now")}
            onPress={dismissBlockedByActiveCycle}
            flex={1}
          />
          <PrimaryButton
            label={t(
              "care.start_treatment.cycle_already_open.go_to_current_cycle",
            )}
            onPress={handleOpenActiveCycle}
            disabled={!blockedByActiveCycle?.onOpenActiveCycle}
            flex={1.1}
          />
        </DialogActions>
      </Dialog>
    </StartTreatmentControllerContext.Provider>
  );
}

export function useStartTreatmentController() {
  const context = useContext(StartTreatmentControllerContext);
  if (!context) {
    throw new Error(
      "useStartTreatmentController must be used inside StartTreatmentControllerProvider",
    );
  }
  return context;
}
